import { DateTime } from 'luxon';

import { DASH } from '@/globals';
import i18n from '@/vueI18n';

import PlanStatus from './types/PlanStatus';

interface IPlanApprovalModel {
  planID: number;
  state: string;
  title: string;
  userName: string;
  comment: string;
  createdOnUtc: Date;
  modifiedOnUtc: Date;
  uuid: string;
  planApprovalID: number;
  archived?: boolean;
  isExpanded?: boolean;
}

type TPlanApprovalToApi = Pick<IPlanApprovalModel, 'uuid' | 'state' | 'title' | 'comment'> & { user_name: string };

class PlanApproval {
  constructor(
    public planID: number,
    public state: string,
    public title: string,
    public userName: string,
    public comment: string,
    public createdOnUtc: Date,
    public modifiedOnUtc: Date,
    public uuid: string,
    public planApprovalID: number,
    public archived?: boolean,
    public isExpanded?: boolean,
  ) {}

  get hasComment() {
    return this.comment != null && this.comment !== '';
  }

  get approvalReceivedAtFormatted() {
    return this.createdOnUtc !== null
      ? DateTime.fromISO(this.createdOnUtc).toLocal().toLocaleString(DateTime.DATETIME_SHORT)
      : DASH;
  }

  get approvalReceivedText() {
    switch (this.state) {
      case PlanStatus.Pending:
        return i18n.global.t('messages.commentReceived');
      case PlanStatus.Approved:
        return i18n.global.t('messages.approvalReceived');
      case PlanStatus.Rejected:
        return i18n.global.t('messages.rejectionReceived');
      default:
        return this.state;
    }
  }

  get status() {
    switch (this.state) {
      case PlanStatus.Pending:
        return 'pendingApproval';
      case PlanStatus.Approved:
        return 'approved';
      case PlanStatus.Rejected:
        return 'rejected';
      default:
        return this.state;
    }
  }

  get statusFormatted() {
    switch (this.state) {
      case PlanStatus.Pending:
        return i18n.global.t('states.pendingApproval');
      case PlanStatus.Approved:
        return i18n.global.t('states.approved');
      case PlanStatus.Rejected:
        return i18n.global.t('states.rejected');
      default:
        return this.state;
    }
  }

  public toApi(): TPlanApprovalToApi {
    return {
      uuid: this.uuid,
      state: this.state,
      title: this.title,
      user_name: this.userName,
      comment: this.comment,
    };
  }

  static fromObject(obj: TPlanApprovalToApi) {
    return new PlanApproval(null, obj.state, obj.title, obj.user_name, obj.comment, null, null, obj.uuid, null);
  }

  static fromApi(apiObject) {
    return new PlanApproval(
      apiObject.plan_id,
      apiObject.state,
      apiObject.title,
      apiObject.user_name,
      apiObject.comment,
      apiObject.created_on_utc,
      apiObject.modified_on_utc,
      null,
      apiObject.plan_approval_id,
      apiObject.archived,
    );
  }
}

export default PlanApproval;
